"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Countdown, EntityDisplay } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import type { TeamDisplayData } from "./TeamDisplay";

/** EntityDisplay styles for large team display (desktop) */
const teamDisplayLgStyles = {
  imageSize: "3xl" as const,
  typography: {
    name: "text-lg sm:text-xl lg:text-2xl font-bold text-(--background-text-color)"
  }
};

/** EntityDisplay styles for small team display (mobile) */
const teamDisplaySmStyles = {
  imageSize: "3xl" as const,
  typography: {
    name: "text-base font-bold text-(--background-text-color)"
  }
};

/** Countdown styles matching the original dark-bg hero design */
const countdownStyles = {
  container: "px-4 sm:px-8",
  cell: "bg-black/30 backdrop-blur-sm rounded-lg px-3 py-2 sm:px-4 sm:py-3 min-w-16 lg:min-w-20",
  typography: {
    value: "text-2xl sm:text-3xl lg:text-4xl font-bold text-(--background-text-color) tabular-nums mb-1.5",
    label: "text-[10px] sm:text-xs text-(--background-text-color-muted) uppercase tracking-wide"
  },
  gap: "md" as const
};
interface CTAViewProps {
  team: TeamDisplayData;
  opponent: TeamDisplayData;
  matchDate: Date;
  gradientFrom: string;
  gradientTo: string;
  onCTAClick: () => void;
  labels: {
    badge: string;
    predictionsCloseIn: string;
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
    ctaButton: string;
    tagline: string;
  };
}
export const CTAView: React.FC<CTAViewProps> = t0 => {
  const $ = _c(58);
  const {
    team,
    opponent,
    matchDate,
    gradientFrom,
    gradientTo,
    onCTAClick,
    labels
  } = t0;
  let t1;
  if ($[0] !== labels.badge) {
    t1 = <span className="inline-block px-4 py-1.5 text-sm font-bold rounded-full bg-white/20 text-(--background-text-color) uppercase tracking-wider mb-6">🎯 {labels.badge}</span>;
    $[0] = labels.badge;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = team.shortName || team.name;
  const t3 = team.assets?.logo;
  let t4;
  if ($[2] !== t3) {
    t4 = <TeamLogo src={t3} size="xl2" />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t4) {
    t5 = <div className="hidden lg:flex flex-1 justify-end"><EntityDisplay name={t2} image={t4} imagePosition="top" styles={teamDisplayLgStyles} /></div>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = team.shortName || team.name;
  const t7 = team.assets?.logo;
  let t8;
  if ($[7] !== t7) {
    t8 = <TeamLogo src={t7} size="xl2" />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t6 || $[10] !== t8) {
    t9 = <EntityDisplay name={t6} image={t8} imagePosition="left" styles={teamDisplaySmStyles} />;
    $[9] = t6;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = opponent.shortName || opponent.name;
  const t11 = opponent.assets?.logo;
  let t12;
  if ($[12] !== t11) {
    t12 = <TeamLogo src={t11} size="xl2" />;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== t10 || $[15] !== t12) {
    t13 = <EntityDisplay name={t10} image={t12} imagePosition="left" styles={teamDisplaySmStyles} />;
    $[14] = t10;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t13 || $[18] !== t9) {
    t14 = <div className="flex lg:hidden items-center justify-center gap-8">{t9}{t13}</div>;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== labels.predictionsCloseIn) {
    t15 = <p className="text-(--background-text-color-muted) text-sm mb-3 uppercase tracking-wide">{labels.predictionsCloseIn}</p>;
    $[20] = labels.predictionsCloseIn;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== labels.days || $[23] !== labels.hours || $[24] !== labels.minutes || $[25] !== labels.seconds) {
    t16 = {
      days: labels.days,
      hours: labels.hours,
      minutes: labels.minutes,
      seconds: labels.seconds
    };
    $[22] = labels.days;
    $[23] = labels.hours;
    $[24] = labels.minutes;
    $[25] = labels.seconds;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== matchDate || $[28] !== t16) {
    t17 = <Countdown targetDate={matchDate} labels={t16} styles={countdownStyles} />;
    $[27] = matchDate;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t15 || $[31] !== t17) {
    t18 = <div className="text-center">{t15}{t17}</div>;
    $[30] = t15;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const t19 = `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`;
  let t20;
  if ($[33] !== t19) {
    t20 = {
      background: t19,
      boxShadow: "0 4px 30px rgba(0,0,0,0.3)"
    };
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== labels.ctaButton || $[36] !== onCTAClick || $[37] !== t20) {
    t21 = <button onClick={onCTAClick} className="px-8 sm:px-12 py-3 sm:py-4 rounded-full text-(--background-text-color) text-lg font-bold cursor-pointer transition-all hover:scale-105 hover:shadow-2xl shadow-lg" style={t20}>{labels.ctaButton} →</button>;
    $[35] = labels.ctaButton;
    $[36] = onCTAClick;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== labels.tagline) {
    t22 = <p className="text-(--background-text-color-muted) text-sm text-center max-w-sm">{labels.tagline}</p>;
    $[39] = labels.tagline;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t14 || $[42] !== t18 || $[43] !== t21 || $[44] !== t22) {
    t23 = <div className="flex flex-col items-center gap-6 lg:gap-8">{t14}{t18}{t21}{t22}</div>;
    $[41] = t14;
    $[42] = t18;
    $[43] = t21;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const t24 = opponent.shortName || opponent.name;
  const t25 = opponent.assets?.logo;
  let t26;
  if ($[46] !== t25) {
    t26 = <TeamLogo src={t25} size="xl2" />;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== t24 || $[49] !== t26) {
    t27 = <div className="hidden lg:flex flex-1 justify-start"><EntityDisplay name={t24} image={t26} imagePosition="top" styles={teamDisplayLgStyles} /></div>;
    $[48] = t24;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t23 || $[52] !== t27 || $[53] !== t5) {
    t28 = <div className="w-full flex flex-col lg:flex-row items-center gap-6 lg:gap-8">{t5}{t23}{t27}</div>;
    $[51] = t23;
    $[52] = t27;
    $[53] = t5;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t1 || $[56] !== t28) {
    t29 = <div className="flex flex-col items-center">{t1}{t28}</div>;
    $[55] = t1;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  return t29;
};